import { createSlice } from "@reduxjs/toolkit";
import { startListening } from "../listenerMiddleware";
import { userStorageKey } from '../../constants/defaultValues';
import { postAuthenticate, postRefreshToken } from "../../services/autenticacao";
import { DefaultError, RefreshTokenError } from "../../constants/messages";
import { TryParseUserStorage } from "../../helpers/util";

const initialState = {
    user: TryParseUserStorage(),
    loading: false,
    errors: [],
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginUser(state) {
            state.loading = true;
            state.errors = []
        },
        loginUserRefresh(state) {
            state.loading = true;
            state.errors = []
        },
        loginUserSuccess(state, action) {
            state.user = action.payload.user;
            state.loading = false;
            state.errors = []
        },
        loginUserError(state, action) {
            state.user = null;
            state.loading = false;
            state.errors = action.payload ?? []
        },
        logoutUser(state, action) {
            const { errors } = action.payload;
            state.user = null;
            state.loading = false;
            state.errors = errors ?? []
        }
    }
});

export const {
    loginUser,
    loginUserRefresh,
    loginUserSuccess,
    loginUserError,
    logoutUser
} = authSlice.actions;

export default authSlice.reducer;

startListening({
    actionCreator: loginUser,
    effect: async (action, listenerApi) => {
        const { navigate, user: userPayload, location } = action.payload;
        const { cpf, password } = userPayload;

        try {
            const loginUser = await loginWithCpfPasswordAsync(cpf, password);
            if (!loginUser.messages) {
                listenerApi.dispatch(loginUserSuccess({ user: loginUser.user, navigate, location }));
            } else {
                listenerApi.dispatch(loginUserError(loginUser.messages));
            }
        } catch (error) {
            listenerApi.dispatch(loginUserError(RefreshTokenError));
        }
    },
});

startListening({
    actionCreator: loginUserRefresh,
    effect: async (action, listenerApi) => {
        const { token, navigate, location } = action.payload;

        try {
            const loginUser = await refreshTokenValidationAsync(token);

            if (loginUser.messages)
                listenerApi.dispatch(loginUserError(loginUser.messages));
            else
                listenerApi.dispatch(loginUserSuccess({ user: loginUser.user, navigate, location }));
        } catch {
            listenerApi.dispatch(loginUserError(RefreshTokenError));
        }
    },
})

startListening({
    actionCreator: loginUserSuccess,
    effect: async (action, listenerApi) => {
        const { user, navigate, location } = action.payload;

        try {
            await loginSuccessAsync(user, navigate, location);
        } catch {
            listenerApi.dispatch(loginUserError(DefaultError));
        }
    },
});

startListening({
    actionCreator: logoutUser,
    effect: async (action, listenerApi) => {
        const { navigate, history } = action.payload;
        await logoutAsync(navigate, history);
    },
});

const loginWithCpfPasswordAsync = async (cpf, password) =>
    await postAuthenticate(cpf, password)
        .then((authUser) => authUser)
        .catch((error) => error);

const refreshTokenValidationAsync = async (refresh_token) =>
    await postRefreshToken(refresh_token)
        .then((authUser) => authUser)
        .catch((error) => error);

const loginSuccessAsync = async (user, navigate, location) => {
    localStorage.setItem(userStorageKey, JSON.stringify(user));
    const redirectTo = location?.state?.from?.pathname || '/';
    navigate(redirectTo);
}

const logoutAsync = async (navigate, history) => {
    localStorage.removeItem(userStorageKey);
    if (navigate) {
        navigate('/')
    }
    else {
        history.push('/');
    }
};